import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../components/common/ConfirmModal';
import toast from 'react-hot-toast';
import { requiredMessage, apiFieldErrors, firstErrorMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const CHURCH_LABELS = { name: "Nom de l'église", pastor: 'Pasteur / Responsable', email: 'Email', phone: 'Téléphone', city: 'Ville', address: 'Adresse' };

const PLAN_LABELS = { free: 'Gratuit', pro: 'Pro', enterprise: 'Entreprise' };
const PLAN_COLORS = { free: '#64748b', pro: '#3b82f6', enterprise: '#7c3aed' };

const s = {
  page: { padding: '0' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 },
  subtitle: { fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' },
  btn: (color = '#3b82f6') => ({
    padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    background: color, color: '#fff', fontWeight: 600, fontSize: '0.875rem',
    display: 'flex', alignItems: 'center', gap: '7px',
  }),
  btnOutline: {
    padding: '7px 14px', borderRadius: '7px', border: '1.5px solid var(--border)',
    background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
    fontSize: '0.8rem', fontWeight: 500,
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '18px' },
  card: (active) => ({
    background: 'var(--card-bg)', borderRadius: '14px', padding: '20px',
    border: `1.5px solid ${active ? 'var(--border)' : '#ef444440'}`,
    opacity: active ? 1 : 0.75, position: 'relative', overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  }),
  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '14px' },
  logo: { width: '52px', height: '52px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 },
  logoPlaceholder: (color) => ({
    width: '52px', height: '52px', borderRadius: '10px', flexShrink: 0,
    background: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: '1.3rem',
  }),
  churchName: { fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 3px' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
    background: `${color}20`, color: color, fontSize: '0.7rem', fontWeight: 700,
  }),
  stats: { display: 'flex', gap: '16px', margin: '12px 0', flexWrap: 'wrap' },
  stat: { textAlign: 'center' },
  statNum: { fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-primary)' },
  statLbl: { fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px', borderTop: '1px solid var(--border)', paddingTop: '12px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal: { background: 'var(--card-bg)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '20px' },
  fieldGroup: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '5px' },
  input: { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '0.875rem', boxSizing: 'border-box' },
  row: { display: 'flex', gap: '12px' },
  modalFooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
};

const PRESET_COLORS = [
  '#1e40af','#4338ca','#7c3aed','#db2777','#dc2626',
  '#d97706','#16a34a','#0891b2','#0f172a','#334155',
];

const CHURCH_TYPES = [
  { value: '', label: 'Non précisé' },
  { value: 'evangelique', label: 'Évangélique' },
  { value: 'catholique', label: 'Catholique' },
  { value: 'protestantisme', label: 'Protestantisme' },
  { value: 'pentecotiste', label: 'Pentecôtiste' },
  { value: 'baptiste', label: 'Baptiste' },
  { value: 'methodiste', label: 'Méthodiste' },
  { value: 'adventiste', label: 'Adventiste' },
  { value: 'autre', label: 'Autre' },
];

const EMPTY_FORM = {
  name: '', pastor: '', city: '', country: "Côte d'Ivoire", address: '',
  phone: '', email: '', website: '', facebook: '', description: '',
  plan: 'free', max_participants: 500, primary_color: '#1e40af',
  use_families: false,
};

export default function SuperAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteChurch, setConfirmDeleteChurch] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [churchErrors, setChurchErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Redirect if not superadmin_platform
  useEffect(() => {
    if (user && user.role !== 'superadmin_platform') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const fetchChurches = useCallback(async () => {
    try {
      const res = await api.get('/churches/');
      setChurches(res.data.results || res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChurches(); }, [fetchChurches]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setLogoPreview(null);
    setChurchErrors({});
    setShowModal(true);
  };

  const openEdit = async (ch) => {
    setEditingId(ch.id);
    setForm({
      name: ch.name || '',
      pastor: ch.pastor || '',
      city: ch.city || '',
      country: ch.country || "Côte d'Ivoire",
      address: ch.address || '',
      phone: ch.phone || '',
      email: ch.email || '',
      website: ch.website || '',
      facebook: ch.facebook || '',
      description: ch.description || '',
      church_type: ch.church_type || '',
      plan: ch.plan || 'free',
      max_participants: ch.max_participants || 500,
      primary_color: ch.primary_color || '#1e40af',
      use_families: false,
    });
    setLogoFile(null);
    setLogoPreview(ch.logo_url || null);
    setChurchErrors({});
    setShowModal(true);
    try {
      const res = await api.get('/notifications/church-settings/', { params: { church_id: ch.id } });
      setForm(p => ({ ...p, use_families: res.data.use_families ?? false }));
    } catch { /* ignore */ }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setChurchErrors({ name: requiredMessage("Nom de l'église") }); return; }
    setChurchErrors({});
    setSaving(true);
    try {
      const { use_families, ...churchFields } = form;
      let church;
      if (editingId) {
        const res = await api.patch(`/churches/${editingId}/`, churchFields);
        church = res.data;
      } else {
        const res = await api.post('/churches/', churchFields);
        church = res.data;
      }
      // Upload logo separately if provided
      if (logoFile && church.id) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        await api.post(`/churches/${church.id}/upload_logo/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      // "Utiliser les Familles" vit dans ChurchSettings, pas dans Church — appel séparé
      const settingsFd = new FormData();
      settingsFd.append('church_id', church.id);
      settingsFd.append('church_name', church.name);
      settingsFd.append('use_families', use_families);
      await api.put('/notifications/church-settings/', settingsFd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { church_id: church.id },
      });
      setShowModal(false);
      fetchChurches();
    } catch (err) {
      const errors = apiFieldErrors(err, CHURCH_LABELS);
      setChurchErrors(errors);
      toast.error(firstErrorMessage(errors, CHURCH_LABELS, 'Erreur lors de la sauvegarde.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (ch) => {
    try {
      await api.post(`/churches/${ch.id}/toggle_active/`);
      fetchChurches();
    } catch {
      // ignore
    }
  };

  const handleDelete = async (ch) => {
    try {
      await api.delete(`/churches/${ch.id}/`);
      setConfirmDeleteChurch(null);
      fetchChurches();
    } catch {
      // ignore
    }
  };

  const totalParticipants = churches.reduce((s, c) => s + (c.participant_count || 0), 0);
  const totalUsers = churches.reduce((s, c) => s + (c.user_count || 0), 0);
  const activeChurches = churches.filter(c => c.is_active).length;

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Chargement...</div>;
  }

  return (
    <div style={s.page}>
      <ConfirmModal
        open={!!confirmDeleteChurch}
        title={`Supprimer "${confirmDeleteChurch?.name}" ?`}
        message="Cette action est irréversible. Tous les données liées à cette église seront supprimées."
        onConfirm={() => handleDelete(confirmDeleteChurch)}
        onCancel={() => setConfirmDeleteChurch(null)}
      />
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>🌐 Super Admin — Plateforme</h1>
          <p style={s.subtitle}>Gestion globale de toutes les églises</p>
        </div>
        <button style={s.btn()} onClick={openCreate}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Nouvelle église
        </button>
      </div>

      {/* Stats globales */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { label: 'Églises actives', value: activeChurches, color: '#22c55e' },
          { label: 'Total fidèles', value: totalParticipants, color: '#3b82f6' },
          { label: 'Utilisateurs', value: totalUsers, color: '#8b5cf6' },
          { label: 'Total églises', value: churches.length, color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: 'var(--card-bg)', borderRadius: '12px', padding: '16px 22px',
            border: '1.5px solid var(--border)', flex: '1', minWidth: '140px',
          }}>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Liste des églises */}
      {churches.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
          Aucune église enregistrée.
        </div>
      ) : (
        <div style={s.grid}>
          {churches.map(ch => (
            <div key={ch.id} style={s.card(ch.is_active)}>
              {!ch.is_active && (
                <div style={{
                  position: 'absolute', top: '10px', right: '10px',
                  background: '#ef4444', color: '#fff', fontSize: '0.65rem',
                  fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                }}>INACTIF</div>
              )}
              <div style={s.cardHeader}>
                {ch.logo_url
                  ? <img src={ch.logo_url} alt="" style={s.logo} />
                  : <div style={s.logoPlaceholder(ch.primary_color || '#1e40af')}>{ch.name[0]}</div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={s.churchName}>{ch.name}</h3>
                  {ch.pastor && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 2 }}>✝ Pasteur {ch.pastor}</div>}
                  {ch.city && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>📍 {ch.city}{ch.country ? `, ${ch.country}` : ''}</div>}
                  <span style={{ ...s.badge(PLAN_COLORS[ch.plan] || '#64748b'), marginTop: '6px', display: 'inline-block' }}>
                    {PLAN_LABELS[ch.plan] || ch.plan}
                  </span>
                </div>
              </div>

              <div style={s.stats}>
                <div style={s.stat}>
                  <div style={s.statNum}>{ch.participant_count ?? 0}</div>
                  <div style={s.statLbl}>Fidèles</div>
                </div>
                <div style={s.stat}>
                  <div style={s.statNum}>{ch.user_count ?? 0}</div>
                  <div style={s.statLbl}>Admins</div>
                </div>
                <div style={s.stat}>
                  <div style={s.statNum}>{ch.max_participants}</div>
                  <div style={s.statLbl}>Max</div>
                </div>
              </div>

              {(ch.email || ch.phone) && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  {ch.email && <span>✉ {ch.email} </span>}
                  {ch.phone && <span>📞 {ch.phone}</span>}
                </div>
              )}

              <div style={s.actions}>
                <button style={s.btn('#3b82f6')} onClick={() => openEdit(ch)}>Modifier</button>
                <button
                  style={s.btn(ch.is_active ? '#f59e0b' : '#22c55e')}
                  onClick={() => handleToggleActive(ch)}
                >
                  {ch.is_active ? 'Désactiver' : 'Activer'}
                </button>
                <button style={s.btn('#ef4444')} onClick={() => setConfirmDeleteChurch(ch)}>Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal création/édition */}
      {showModal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ ...s.modal, maxWidth: '680px', padding: 0, overflow: 'hidden' }}>
            {/* En-tête modal avec aperçu */}
            <div style={{ background: `linear-gradient(135deg, ${form.primary_color} 0%, ${form.primary_color}cc 100%)`, padding: '24px 28px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ position: 'relative' }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'cover', border: '3px solid rgba(255,255,255,0.4)' }} />
                    : <div style={{ width: 64, height: 64, borderRadius: 14, background: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1.6rem' }}>
                        {form.name[0]?.toUpperCase() || '?'}
                      </div>
                  }
                  <label style={{ position: 'absolute', bottom: -4, right: -4, background: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={form.primary_color} strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  </label>
                </div>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                    {editingId ? 'Modifier l\'église' : 'Nouvelle église'}
                  </div>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.2 }}>
                    {form.name || 'Nom de l\'église'}
                  </div>
                  {form.city && <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', marginTop: 2 }}>📍 {form.city}{form.country ? `, ${form.country}` : ''}</div>}
                </div>
              </div>
            </div>

            <div style={{ padding: '24px 28px', maxHeight: '60vh', overflowY: 'auto' }}>
              {/* Identité */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                  Identité
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={s.label}>Nom de l'église<Req /></label>
                    <input style={{ ...s.input, ...(churchErrors.name ? { borderColor: ERROR_COLOR } : {}) }} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Église de la Grâce" autoFocus />
                    <FieldError message={churchErrors.name} />
                  </div>
                  <div>
                    <label style={s.label}>Pasteur / Responsable</label>
                    <input style={s.input} value={form.pastor} onChange={e => setForm(p => ({ ...p, pastor: e.target.value }))} placeholder="Ex: Pasteur Jean Kouassi" />
                  </div>
                  <div>
                    <label style={s.label}>Type d'église</label>
                    <select style={{ ...s.input, backgroundColor: 'var(--card-bg)' }} value={form.church_type || ''} onChange={e => setForm(p => ({ ...p, church_type: e.target.value }))}>
                      {CHURCH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={s.label}>Description / Slogan</label>
                    <input style={s.input} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Ex: Une église au cœur de la communauté..." />
                  </div>
                </div>
              </div>

              {/* Localisation */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  Localisation
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={s.label}>Ville</label>
                    <input style={s.input} value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Ex: Abidjan" />
                  </div>
                  <div>
                    <label style={s.label}>Pays</label>
                    <input style={s.input} value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} placeholder="Côte d'Ivoire" />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={s.label}>Adresse complète</label>
                    <input style={s.input} value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Ex: Cocody, Rue des Jardins, près du marché..." />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 14a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 3h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  Contact & Web
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={s.label}>Téléphone</label>
                    <input style={s.input} value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+225 07 XX XX XX XX" />
                  </div>
                  <div>
                    <label style={s.label}>Email</label>
                    <input style={s.input} type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="contact@eglise.ci" />
                  </div>
                  <div>
                    <label style={s.label}>Site web</label>
                    <input style={s.input} value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
                  </div>
                  <div>
                    <label style={s.label}>Facebook / Réseaux</label>
                    <input style={s.input} value={form.facebook} onChange={e => setForm(p => ({ ...p, facebook: e.target.value }))} placeholder="facebook.com/eglise" />
                  </div>
                </div>
              </div>

              {/* Abonnement & Config */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  Abonnement & Apparence
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={s.label}>Plan</label>
                    <select style={{ ...s.input, backgroundColor: 'var(--card-bg)' }} value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}>
                      <option value="free">🆓 Gratuit</option>
                      <option value="pro">⭐ Pro</option>
                      <option value="enterprise">💎 Entreprise</option>
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Max participants</label>
                    <input style={s.input} type="number" min="10" value={form.max_participants} onChange={e => setForm(p => ({ ...p, max_participants: parseInt(e.target.value) || 500 }))} />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={s.label}>Couleur principale</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                      {PRESET_COLORS.map(c => (
                        <button key={c} onClick={() => setForm(p => ({ ...p, primary_color: c }))}
                          style={{ width: 30, height: 30, borderRadius: 8, background: c, border: form.primary_color === c ? '3px solid #fff' : '2px solid transparent', cursor: 'pointer', boxShadow: form.primary_color === c ? `0 0 0 2px ${c}` : 'none' }} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input type="color" value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} style={{ width: 40, height: 36, borderRadius: 7, border: '1.5px solid var(--border)', cursor: 'pointer', padding: 2, flexShrink: 0 }} />
                      <input style={{ ...s.input, flex: 1 }} value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} placeholder="#1e40af" />
                    </div>
                  </div>
                  <div style={{ gridColumn: '1/-1', backgroundColor: 'var(--page-bg)', borderRadius: '10px', padding: '14px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>Utiliser les Familles</div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        La famille devient obligatoire à l'inscription et apparaît sur la carte de membre
                      </div>
                    </div>
                    <div
                      onClick={() => setForm(p => ({ ...p, use_families: !p.use_families }))}
                      style={{
                        width: '44px', height: '24px', borderRadius: '999px', cursor: 'pointer', flexShrink: 0,
                        backgroundColor: form.use_families ? '#7c3aed' : '#d1d5db',
                        position: 'relative', transition: 'background-color 0.2s',
                      }}>
                      <div style={{
                        position: 'absolute', top: '3px',
                        left: form.use_families ? '23px' : '3px',
                        width: '18px', height: '18px', borderRadius: '50%',
                        backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left 0.2s',
                      }}/>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ ...s.modalFooter, padding: '16px 28px', borderTop: '1px solid var(--border)', margin: 0, background: 'var(--card-bg)' }}>
              <button style={s.btnOutline} onClick={() => setShowModal(false)}>Annuler</button>
              <button style={{ ...s.btn(form.primary_color), minWidth: 130 }} onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving
                  ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Enregistrement...</>
                  : editingId ? '✓ Enregistrer les modifications' : '+ Créer l\'église'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
